/**
 * 管理后台布局 (company_admin / super_admin): /dashboard, /cost, /products/*, /billing, /admin/*。
 *
 * 左侧分组导航 概览 / 业务 / 管理 (原 /admin 页内的子 tab 也并入这里, 可见性规则不变):
 *   余额充值   仅 company_admin 且开启余额计费
 *   公司 / 提示词 / LLM 配置   仅 super_admin
 *   邀请       useCanInvite (company_admin + 非 single 部署 + 非个人公司)
 * 顶栏: 当前位置 + 「返回前台」 + 用户菜单。手机: 侧栏变抽屉 (ui-store.sidebarOpen)。
 * 既可作为布局路由 (渲染 <Outlet/>), 也可直接包 children。
 */
import { useEffect, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  BarChart3, Bot, Building2, ChevronRight, LayoutDashboard, Mail, Menu, MessageSquareText, Package, Users, Wallet, X,
  type LucideIcon,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { isMobileViewport, useUiStore } from "@/stores/ui-store";
import { useCanInvite } from "@/features/admin/invites/InvitesRoute";
import { buttonVariants } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { BalanceChip } from "./BalanceChip";
import { BrandMark } from "./BrandMark";
import { UserMenu } from "./UserMenu";
import { useShell } from "./useShell";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** 仅 company_admin 且开启余额计费 */
  billingOnly?: boolean;
  superOnly?: boolean;
  inviteOnly?: boolean;
}

const NAV_GROUPS: Array<{ title: string; items: NavItem[] }> = [
  {
    title: "概览",
    items: [
      { to: "/dashboard", label: "仪表盘", icon: LayoutDashboard },
      { to: "/cost", label: "成本统计", icon: BarChart3 },
    ],
  },
  {
    title: "业务",
    items: [
      { to: "/products", label: "产品", icon: Package },
      { to: "/billing", label: "余额充值", icon: Wallet, billingOnly: true },
    ],
  },
  {
    title: "管理",
    items: [
      { to: "/admin/users", label: "用户", icon: Users },
      { to: "/admin/companies", label: "公司", icon: Building2, superOnly: true },
      { to: "/admin/invites", label: "邀请", icon: Mail, inviteOnly: true },
      { to: "/admin/prompts", label: "提示词", icon: MessageSquareText, superOnly: true },
      { to: "/admin/llm-config", label: "LLM 配置", icon: Bot, superOnly: true },
    ],
  },
];

function useVisibleGroups() {
  const role = useAuthStore((s) => s.user?.role);
  const { showBilling } = useShell();
  const canInvite = useCanInvite();
  const isSuper = role === "super_admin";
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items
      .filter((it) => (!it.billingOnly || showBilling) && (!it.superOnly || isSuper) && (!it.inviteOnly || canInvite))
      // 成本 (USD) 只有超管看得到; 公司管理员这里只是用量
      .map((it) => (it.to === "/cost" && !isSuper ? { ...it, label: "用量统计" } : it)),
  })).filter((g) => g.items.length > 0);
}

/** 当前路径对应的 [分组, 菜单项] (最长前缀匹配) */
function useCurrentItem(groups: ReturnType<typeof useVisibleGroups>) {
  const { pathname } = useLocation();
  let best: { group: string; item: NavItem } | null = null;
  for (const g of groups) {
    for (const it of g.items) {
      if ((pathname === it.to || pathname.startsWith(`${it.to}/`)) && (!best || it.to.length > best.item.to.length)) {
        best = { group: g.title, item: it };
      }
    }
  }
  return best;
}

export function AdminLayout({ children }: { children?: ReactNode }) {
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebar = useUiStore((s) => s.setSidebar);
  const { pathname } = useLocation();
  const groups = useVisibleGroups();
  const current = useCurrentItem(groups);

  // 手机: 抽屉在切换页面后自动收起
  useEffect(() => {
    if (isMobileViewport()) setSidebar(false);
  }, [pathname, setSidebar]);

  // 抽屉打开时 Esc 收起
  useEffect(() => {
    if (!sidebarOpen || !isMobileViewport()) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setSidebar(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen, setSidebar]);

  return (
    <div className="flex h-[100dvh] bg-background">
      {/* 手机: 抽屉遮罩 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 animate-in fade-in-0 md:hidden"
          onClick={() => setSidebar(false)}
          aria-hidden="true"
        />
      )}
      <aside
        aria-label="后台导航"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r bg-card transition-transform duration-200",
          "md:static md:z-auto md:w-60 md:translate-x-0",
          sidebarOpen ? "translate-x-0 shadow-xl md:shadow-none" : "-translate-x-full max-md:invisible",
        )}
      >
        <div className="flex h-14 shrink-0 items-center justify-between gap-2 bg-gradient-to-br from-brand to-[#43a047] px-4">
          <BrandMark inverse to="/dashboard" subtitle="管理后台" />
          <button
            type="button"
            onClick={() => setSidebar(false)}
            aria-label="收起导航"
            className="rounded-md p-1.5 text-white/90 hover:bg-white/15 md:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {groups.map((g) => (
            <div key={g.title}>
              <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {g.title}
              </div>
              <div className="space-y-0.5">
                {g.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    className={({ isActive }) =>
                      cn(
                        "relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isActive
                          ? "bg-primary-soft font-medium text-primary before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-brand"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )
                    }
                  >
                    <it.icon aria-hidden className="h-4 w-4 shrink-0" />
                    {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="shrink-0 border-t p-3 md:hidden">
          <Link to="/" className={cn(buttonVariants({ variant: "outline", size: "sm" }), "w-full")}>
            返回前台
          </Link>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="z-20 flex h-14 shrink-0 items-center gap-2 bg-gradient-to-br from-brand to-[#43a047] px-3 text-white shadow-[0_2px_8px_rgba(56,142,60,0.25)] md:px-6">
          <button
            type="button"
            onClick={() => setSidebar(true)}
            className="rounded-md p-2 text-white hover:bg-white/15 md:hidden"
            aria-label="打开导航"
          >
            <Menu className="h-5 w-5" />
          </button>
          {current && (
            <div className="flex min-w-0 items-center gap-1.5 text-sm">
              <span className="hidden text-white/80 sm:inline">{current.group}</span>
              <ChevronRight aria-hidden className="hidden h-3.5 w-3.5 text-white/70 sm:block" />
              <span className="truncate font-semibold text-white">{current.item.label}</span>
            </div>
          )}
          <div className="flex-1" />
          <BalanceChip inverse className="hidden sm:inline-flex" />
          <Link to="/" className="hidden h-8 items-center rounded-full border border-white/30 bg-white/15 px-3 text-sm font-medium text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:inline-flex">
            返回前台
          </Link>
          <UserMenu inverse />
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}
